// src/utils/downloadBlob.ts
import type { AxiosResponse } from 'axios';

/**
 * The filename carried by a `Content-Disposition` header (e.g.
 * `inline; filename="annual-return-L1234-2026-ugle.pdf"`), or null when the
 * header is absent or names no file.
 */
export function filenameFromContentDisposition(
  disposition: string | null | undefined,
): string | null {
  return disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? null;
}

/**
 * Hand a `responseType: 'blob'` response to the browser as a file download.
 * The filename comes from the response's `Content-Disposition` header so the
 * saved file is named by the API; `fallbackFilename` is used when the header
 * is missing or unreadable.
 */
export function downloadBlobResponse(
  response: AxiosResponse<Blob>,
  fallbackFilename: string,
): void {
  const disposition = response.headers['content-disposition'] as
    string | undefined;
  const filename =
    filenameFromContentDisposition(disposition) ?? fallbackFilename;

  const blobUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
}

/**
 * With `responseType: 'blob'` an error body arrives as a Blob too, so the
 * axios interceptor cannot read its `message`. Parse it back to JSON and return
 * the API's message, or null when the body is not a Blob or carries none.
 */
export async function readBlobErrorMessage(
  data: unknown,
): Promise<string | null> {
  if (!(data instanceof Blob)) return null;
  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'message' in parsed &&
      typeof parsed.message === 'string'
    ) {
      return parsed.message;
    }
  } catch {}
  return null;
}
